/**
 * The bento box primitive's contract (CONTRACT v1.2, ported from A v2.2).
 *
 * A box answers before it asks. Its face states the answer in one look; its
 * body, opened in place, does the job; a task's foot shows what will happen
 * and commits once; afterwards the face is the receipt.
 *
 * Three kinds: Fact tells, List finds, Task changes (K1). Availability is a
 * read state, a provider is an owner, history is a read-only List or Fact and
 * identity is a tone. `ui/box.tsx` draws exactly these fields, and the types
 * refuse the shapes the contract forbids:
 *   - a figure outside a `Read` (R1, R5);
 *   - a `TaskBox` with more than one commit (K2);
 *   - a part without an owner (C4);
 *   - a free-text unchanged, pending or dry-run-needed blocker (O6, O8, C5);
 *   - a retry on a not-confirmed result (W1);
 *   - a face act that commits (F4);
 *   - a bulk Task whose scope isn't its List's selection (R6).
 * `box.schema.test.ts` proves each refusal with `@ts-expect-error`.
 */
import type { ReactNode } from "react";

import type { TabDraft } from "@/components/ui/box-draft";

type Route = string;
export type PartId = string;

/* ------------------------------------------------------------------ */
/* 1. What every box shares                                            */
/* ------------------------------------------------------------------ */

/** Who owns a record. A provider record is evidence with a read time, never IMAM state (C4, ADR 0026). */
/** A provider's `readAt` is the ISO instant its record was read; the kit says it in its own words (C5, CASE-039). */
export type FactOwner =
  | { system: "imam" }
  | { system: "classroom" | "drive" | "zoom" | "gmail" | "google_chat"; readAt: string | null };

/**
 * A read is never a number until it is ready (R1, R3). Loading, failed,
 * unread and empty are different, and none of them is zero. A forbidden box
 * isn't drawn at all. A stale read keeps its last value with its read time
 * and offers Refresh (R2). `readAt` is the ISO instant of the read; the kit
 * formats it, and no area does (CASE-039).
 */
export type Read<T> =
  | { status: "loading" }
  | { status: "ready"; value: T; readAt: string }
  /** The stale mark says "As of {readAt}", so `sentence` carries no time: "Couldn't refresh. A later change isn't shown." */
  | { status: "stale"; last: T; readAt: string; sentence: string; refresh: () => void }
  | { status: "empty"; sentence: string; sub?: string }
  | { status: "failed"; sentence: string; detail?: string; retry: () => void }
  | { status: "unread"; sentence: string; sub?: string }
  | { status: "forbidden" };

export type ReadStatus = Read<unknown>["status"];

/**
 * The six state shapes, always drawn with their word (F2). The word is the
 * job's own ("Roll not taken", "Returned for revision"); the class picks the
 * shape, so colour is never the only cue.
 */
export type StateClass =
  | "waiting" // the person's act is due here: solid dot
  | "draft" // changed, not committed: half dot
  | "done" // settled: check
  | "quiet" // nothing to do yet: ring
  | "reading" // reading, or not read: dashed ring
  | "failed"; // a read or a commit failed: diamond

export interface BoxState {
  class: StateClass;
  word: string;
}

/**
 * A figure drawn from a read (R1). The kit calls a box's `figures` with the
 * ready value, or a stale read's last value, and never otherwise; there is no
 * other way to put a figure on a box.
 */
export interface Figure {
  value: number;
  unit: string;
}

/**
 * Why an act waits. Each names the part that fixes it, so the sentence links
 * to it (F5, O3). The typed kinds' words are the kit's, never free text.
 */
export type Blocker =
  /**
   * "1 of 2 not marked." `field` names the one control that repairs it, so the
   * reason links to that field rather than to its whole part (S1A-3).
   */
  | { kind: "missing"; part: PartId; sentence: string; field?: { label: string; focus: () => void } }
  /** A draft equal to what it started from (O6). */
  | { kind: "unchanged"; part: PartId; attempt?: number }
  | { kind: "conflict"; part: PartId; sentence: string } // "Choose which roll to keep."
  /** The commit's own save is in flight (O8). The kit draws it while the commit runs; nothing but the result fixes it. */
  | { kind: "pending"; part?: never }
  /**
   * A source waits for a dry run of exactly this source, and any change to the
   * source voids it (C5). `part` is where the dry run is read.
   */
  | { kind: "dry-run-needed"; part: PartId };

/** The one sentence a blocker shows. */
export function blockerSentence(blocker: Blocker): string {
  switch (blocker.kind) {
    case "unchanged":
      return blocker.attempt === undefined ? "No changes yet." : `No changes since attempt ${blocker.attempt}.`;
    case "pending":
      return "Saving, wait for the result.";
    case "dry-run-needed":
      return "Preview this source first.";
    default:
      return blocker.sentence;
  }
}

/**
 * The one act per box that takes part in the page's ranking (P2). Eligible,
 * blocked and rank are separate facts: a blocked act is still eligible; a
 * forbidden act isn't drawn; an unranked candidate can't prove it is lowest.
 */
export interface Candidate {
  eligible: boolean;
  blocked: Blocker | null;
  /** The server's rank; lowest leads. `null` is unranked. */
  rank: number | null;
}

/** A visited box's address, `<object>:<id>[/<job>]`, as F5 writes it (O10). */
export type VisitAddress = `${string}:${string}`;

/**
 * A face act opens its box, reads safely, visits a box in place or leaves for
 * a provider; it never commits (F4). An exit with an internal `to` still loads
 * another page, until each area moves it to a visit.
 */
export type FaceAct = { verb: string } & (
  /** `opens`: the part an open act opens and focuses. */
  | { does: "open"; opens?: PartId }
  /** `run`: what a check act reads. */
  | { does: "check"; run?: () => void }
  /** The host shows the addressed box in this box's place (O10); `next` steps to the next record at the same level (CASE-031). */
  | { does: "visit"; visit: VisitAddress; next?: true }
  /** A provider's own page, in a new tab; the verb names the provider: "Join in Zoom". */
  | { does: "leave"; providerUrl: string; run?: never }
  /**
   * A provider's window the area's own adapter opens, such as Google's consent
   * for Connect Classroom. The kit calls `run` within the press, so the window
   * isn't blocked, and holds the act until it settles. It is never a commit,
   * and nothing it resolves passes through the kit: the area says the outcome.
   */
  | { does: "leave"; run: () => Promise<void>; providerUrl?: never }
  | { does: "exit"; to?: Route; external?: boolean }
);

/** Named by destination, at most two, always quiet. */
export interface Exit {
  label: string;
  to?: Route;
  external?: boolean;
  /** An in-place exit, such as Show or Update roll, reopens the box. */
  reopens?: boolean;
  // An exit with an IMAM `to` is page navigation (data-nav, CASE-032, CASE-040); the open head draws it too.
  /** A visit from a resting line: the host shows that box in this box's place (O10). */
  visit?: VisitAddress;
  /** The visit steps to the next record at the same level (CASE-031). */
  next?: true;
}

/**
 * settled: rests on its face once settled; a box the person opened stays open.
 * choice: rests until the person opens it.
 * never: always open (the one job a page exists for); no Hide.
 */
export type Fold = "settled" | "choice" | "never";

interface BoxBase<T> {
  /** Content inside an ordered record disclosure: no nested card or legacy ready hero. */
  presentation?: "embedded";
  id: string;
  /** One sentence, not drawn; reviews and audits read it. */
  job: string;
  /** Tasks are named for the act: Prepare, Teach, Close, Hand in. */
  label: string;
  /** The page the label opens, such as Admissions: page navigation (`data-nav`), never an act (CASE-032). */
  labelTo?: Route;
  owner: FactOwner;
  read: Read<T>;
  /** The closed face's one line under the answer or the state: "2 learners · recap not written". */
  detail(value: T): string;
  /**
   * The open head's one line, only for a fact no part and no effect states:
   * "Session 9 is already held". Without it the open head is the label and the
   * state alone, and the closed detail gives way to the parts (CASE-025).
   */
  openDetail?(value: T): string | undefined;
  /** A second, quieter line under the detail. */
  sub?(value: T): string;
  /** The face's one act (F1). */
  act?: FaceAct;
  /** The act this box offers to the page's ranking (P2). */
  candidate?: Candidate;
  exits?: [Exit] | [Exit, Exit];
  fold: Fold;
  /** The face's share of a row; an open box always takes the row. */
  width?: 1 | 2;
  /**
   * An IMAM record says when it was read, "Read Today, 9:12 AM", where a
   * provider's says "Classroom · read ...": for a record several people edit,
   * such as Settings. Most IMAM boxes are the person's own working state, so
   * a time on each would be noise (CASE-039).
   */
  readTime?: true;
}

/* ------------------------------------------------------------------ */
/* 2. The three kinds                                                  */
/* ------------------------------------------------------------------ */

/**
 * Fact: tells. No commit, no foot, and no state of its own: a ready Fact's
 * answer (figures, prose or an empty read's sentence) is its headline, and
 * only a read that isn't ready draws a mark. A Fact never rests (F1, F3,
 * CASE-023). A box with no commit is a Fact; its exit may be its candidate.
 * A box whose only writes are field saves is a Fact too: its answer is its
 * fields, each with its own save word (K1, CASE-026).
 */
export interface FactBox<T> extends BoxBase<T> {
  kind: "fact";
  /** Up to four, all from this one read. */
  figures?(value: T): Figure[];
  /** Published prose, the headline of a ready Fact. */
  prose?(value: T): string;
  /** Full text or a fact list, drawn when open. */
  body?(value: T): ReactNode;
  /** Values the person edits where they're drawn, drawn when open; such a Fact has `fold: "never"`. */
  fields?(value: T): FactField[];
  /** identity: a course edition's cover (ADR 0035), with its hue. */
  tone?: { identity: string };
}

/**
 * A value edited where it's drawn, in a Fact (CASE-026). Leaving it, or Enter,
 * saves only this field, and Esc restores the saved value (O3). It says its
 * own Saving, Saved or Not saved there, never a toast. It isn't an act: no
 * `data-act`, candidate or lead, and it never saves, validates away or
 * discards anything on its own (O8).
 */
export interface FactField {
  id: string;
  /** The person's word for the value: "Preferred name". */
  label: string;
  /** The area's control. It names itself by `label` (the kit gives it `aria-labelledby`) and spreads `aria` on itself. */
  render: (labelledBy: string, aria: FieldAria) => ReactNode;
  save: FieldSave;
  /** Who can see the value, in the field's own note (C1). */
  note?: string;
  /** A newer version, at the field it changed: R4's two named choices; nothing saves until one is chosen. */
  conflict?: Conflict;
}

/** A row's quiet act. A row act never leads the page. */
export type RowAct = { verb: string } & (
  | { does: "open" }
  | { does: "exit"; to?: Route }
  | { does: "visit"; visit: VisitAddress; next?: true }
  /**
   * A safe look at this row that changes nothing, such as Review course
   * choosing the course the page's intake Task reads for. It never commits
   * and loads no page (F4).
   */
  | { does: "check"; run: () => void }
  /** A provider's own page for this row, in a new tab; the verb names the provider (F4). */
  | { does: "leave"; providerUrl: string }
);

/**
 * A row's own work, opened in place (K3): its own effects, blockers, commit
 * and result. It is part of the row, never a box of its own.
 */
export interface RowTask {
  /** The row task's own writable content, such as a judgement and a comment. */
  render?: () => ReactNode;
  /**
   * Its named parts, drawn after `render` in time order, each with its own
   * state, owner and draft. A blocker names one so its reason links to the fix
   * (F5). A part's id is unique in its box, so a row's part ids carry its key.
   */
  parts?: Part[];
  commit: Commit;
  result: Result;
}

/** A table List's column: "Midterm", with a quieter second line such as "30% · from coursework". */
export interface ListColumn {
  id: string;
  label: string;
  sub?: string;
}

/**
 * A row's cell under one column (O9). A read cell is text: a course %, a score
 * another owner feeds, an attendance mark. An editable cell is the area's
 * control: it saves when it loses focus, restores the saved value on Esc, and
 * sends its own version and key (R4, W1). The kit names it by its column and
 * its row, draws its save word in the cell and moves focus between cells; a
 * key the control handles itself stops there.
 */
export type ListCell =
  | { id: string; read: string }
  | { id: string; render: (labelledBy: string, aria: FieldAria) => ReactNode; save: FieldSave };

/** List: finds. Rows share one template; a row opens in place (K3). */
export interface ListBox<T, R> extends BoxBase<T> {
  kind: "list";
  /** Working register: the page owns its ready identity, with no repeated head/hero. */
  working?: boolean;
  /** Replace only native find/facets with controls bound to this same read/controller. */
  renderTools?(value: T): ReactNode;
  /** Called with the ready value, or a stale read's last value (drawn with its read time). */
  state(value: T): BoxState;
  rows(value: T): R[];
  /** Area presentation over this List read; ready zero rows retain selection and recovery tools. Native ownership and read failures remain here. */
  renderRows?(value: T): ReactNode;
  /**
   * The row whose act is this List's candidate, by its key, as the server's
   * ranking projection names it (P2, CASE-032); the box's `candidate` holds its
   * eligibility and rank. That row's visit, leave or open is drawn as an act
   * that can lead, and every other row act stays quiet. A row Task the person
   * opened is the candidate instead (P3), and hiding it gives the offer back.
   * A named row that isn't drawn leaves the List with no candidate; the client
   * never picks another (CASE-045).
   */
  offers?: string;
  row: {
    /**
     * The row's identity for the kit: its open state, its send in flight and
     * where focus lands on its result are all kept by this key. It stays the
     * same across a create that gives the record its server id, so a row made
     * by its own commit keeps its foot and its result (W2, P5).
     */
    key(row: R): string;
    title(row: R): string;
    /** The workspace the row's name opens, such as its course or session: page navigation (`data-nav`), never an act (CASE-032). */
    nav?(row: R): Route | undefined;
    /**
     * The record the row visits when that is its one destination, as `nav` is
     * for a page: in `lines` or `covers` the whole row presses it, and it draws
     * no act word (O10, CASE-048). A row has `nav` or `visit`, never both.
     */
    visit?(row: R): VisitAddress | undefined;
    /** In `lines`, the end cell's words, such as the session's time; the area says the zone only where the List spans several (C2). */
    end?(row: R): string | undefined;
    /** In `covers`, the course's CRN: said beside its code only where two covers share a code (CASE-048). */
    crn?(row: R): string | undefined;
    /**
     * The row's course code, when the row is a course: its edition tone, the
     * course's signature mark, is drawn before its name, as in any course
     * register (K1, ADR 0035). The mark is decorative; the name says which.
     */
    course?(row: R): string | undefined;
    /** Only a row that differs from its neighbours is marked: a row that reads like the others has none (K3, CASE-009). */
    state?(row: R): BoxState | null;
    facts?(row: R): string;
    act?(row: R): RowAct | undefined;
    /** Read-only detail shown in the row: no foot, no candidate, no box (K3). */
    detail?(row: R): ReactNode;
    /** The row's own task, drawn inside the row with its own foot (K3). */
    task?(row: R): RowTask | undefined;
    /** Whether the row starts open in place. */
    startsOpen?(row: R): boolean;
    /** With `table`, the row's cells: one per column, in the columns' order (O9). */
    cells?(row: R): ListCell[];
    /**
     * The row's own controls beside its mark, such as its selection or a quick
     * score: each edits a draft and is never an act. A change in one gives the
     * List intent, as any edit in a box does (P3).
     */
    controls?(row: R): ReactNode;
    /**
     * The group line a row sits under, such as its day or "Earlier courses"
     * (A1 C27, L05; A2 C10). One quiet line is drawn where the group changes,
     * in the rows' own order: the kit never sorts, so a group's rows sit
     * together, and rows with no group come first. A group line is never a
     * row, an act or a count; the head keeps the one count (R5).
     */
    group?(row: R): string | undefined;
  };
  /**
   * A List read as a table (O9): `title` heads the rows' own column
   * ("Learner"), each column names a field, and `note` is said once where the
   * cells are edited, such as what a learner sees while a score saves.
   */
  table?: { title: string; columns: ListColumn[]; note?: string };
  /**
   * How a Home draws its rows (CASE-048). Without it, a row is its name, its
   * facts under it and its end.
   * - `lines`: each row is one line in columns aligned down the List: the
   *   course's mark and code, the name, which takes the free width, and the end
   *   cell, its `end` and its mark. It draws no facts. A row with one
   *   destination, its `nav` or `visit`, is that destination across its width;
   *   an act word it draws stays a control of its own.
   * - `covers`: each row with no group is a course's edition cover: its art and
   *   code, its title, its facts as one line, its mark and its act. The whole
   *   cover is its `nav`. The List draws no count and no frame, and its grouped
   *   rows follow as one folded group with its count, drawn as `lines`, open at
   *   first only when there is no cover.
   */
  layout?: "lines" | "covers";
  /**
   * A Home's List (CASE-048): its head is one line, the label, a ready read's
   * count bare after it, "This week 3", and its exit at the line's end. Its
   * ready state is `quiet`, and its word is the count; a read that isn't ready
   * draws its mark and word, never a number (F1, R5).
   */
  home?: true;
  /** Counts from this List's own read, drawn in its head under that read's state (R5). */
  figures?(value: T): Figure[];
  /** Server-side find, the List's own tool: it never commits and never changes the selection (R6). */
  find?: {
    placeholder: string;
    /** Filters beside the find, such as active or archive, program and term (R6). */
    facets?: ListFacet[];
    /**
     * The query the List reads for, when its page holds it, such as one kept in
     * the address so a reload or Back restores the reading. The find shows what
     * the person types and hands each edit to onQueryChange. Whenever this
     * changes, to a reload's, Back's, a cleared filter's or the page's own echo,
     * the find shows it, so the page hands back what it received, latest last,
     * and normalises its read, never the field. Without it, the find keeps what
     * the person types (R6).
     */
    query?: string;
    /** The query as the person types it; the List reads its rows for it, and its selection stays (R6). */
    onQueryChange?(query: string): void;
    /** How many of the read's rows the find and filters hide; the head says "N not shown by this filter". */
    hidden?: number;
  };
  /** Whether a row task holds an unsent draft (F3, O7). */
  draft?: boolean;
}

/** One of a List's filters: a labelled choice that narrows its read, never an act (R6). */
export interface ListFacet {
  /** The choice's name, such as "Program". */
  label: string;
  value: string;
  /** Only the chosen option shows, so each option says its filter: "All programs", "Archive". */
  options: { value: string; label: string }[];
  /** The List reads its rows for the new value; its selection stays (R6). */
  onChange(value: string): void;
  /**
   * Where the options come from a read of their own, such as programs or
   * terms: while it reads, the choice waits; if it fails, the sentence says so
   * beside the choice with Try again, so nobody narrows on a list that is
   * missing options (R1).
   */
  read?: { status: "loading" } | { status: "failed"; sentence: string; retry: () => void };
}

/**
 * A value the person can change back in place, saved in its own field (O3,
 * CASE-013). Persistence is not a commit: it shows its own word there, never
 * a toast and never in the foot.
 */
export type FieldSave =
  | { status: "idle" }
  | { status: "saving" }
  | { status: "saved" }
  /** The server refused this value and says why. The fix is the field, so nothing offers to send it again (O3, C3). */
  | { status: "refused"; sentence: string }
  /** It didn't reach the server: the text is kept, and Retry sends it again. */
  | { status: "failed"; sentence: string; retry: () => void }
  /** Sent, and no answer came back: it may have saved. Check reads it; saving again is never offered. */
  | { status: "unknown"; check: () => void };

/**
 * What the kit tells a field's control, to spread on it: a refused value is
 * invalid and described by its reason (O3).
 */
export type FieldAria = { "aria-invalid"?: true; "aria-describedby"?: string };

export interface Part {
  id: PartId;
  /** "Roll", "Recap and next steps", "Recording". */
  label: string;
  /** Each part has its own state (O2). */
  state: BoxState;
  /**
   * The box's own state already says this part's state, so the part draws no
   * mark of its own: "Roll marked, not recorded" on the head owns the roll's
   * "Not recorded" (CASE-030). `state` stays, for the part's own reading.
   */
  markedByBox?: true;
  /** Every part names its owner; a provider part says who owns it (C4). */
  owner: FactOwner;
  fold: Fold;
  /**
   * Where unsaved work survives an interruption. Unless "none", the kit keeps
   * the part's content drawn, hidden, while the box or the part is hidden, so
   * a control's own draft survives Hide (O7).
   */
  draft: "browser" | "server" | "none";
  /**
   * The browser draft this part edits, kept by the kit in this tab (CASE-038).
   * Kept, it holds the box's draft; kept in this tab's storage, a hidden box
   * or row says "Draft kept in this tab"; a confirmed commit ends it.
   */
  keep?: Pick<TabDraft<unknown>, "kept" | "inTab" | "sending">;
  /** One line drawn under the header, and the whole part while folded. */
  summary?: string;
  /** The record changed after the draft began: two named choices; nothing commits until chosen (R4). */
  conflict?: Conflict;
  render?: () => ReactNode;
}

/** What changed since the person's read, and two named choices (R4). */
export interface Conflict {
  sentence: string;
  /** Their version, fact by fact, beside the person's own entry: at least one, so there is something to compare (R4). */
  facts: [{ label: string; theirs: string }, ...{ label: string; theirs: string }[]];
  keepMine: { label: string; run: () => void };
  loadNewer: { label: string; run: () => void };
}

declare const selected: unique symbol;

/**
 * A List's selection (R6). It belongs to the List and lasts across filtering
 * and paging until the commit or until the person clears it; it clears on a
 * confirmed commit, and narrows to the failed records on a partial one. Only
 * `selectionOf` builds one, so a bulk Task's scope can't be a free count.
 */
export interface Selection {
  /** The List's box id. */
  readonly list: string;
  readonly keys: readonly string[];
  /** How many selected rows the current filter hides. */
  readonly hidden: number;
  readonly [selected]: true;
}

/** The selection of a List: its row keys, and how many of them the current filter hides (R6). */
export function selectionOf(list: Pick<ListBox<unknown, unknown>, "kind" | "id">, keys: readonly string[], hidden: number): Selection {
  return { list: list.id, keys, hidden } as unknown as Selection;
}

/**
 * A bulk Task's scope (R6): its List's selection, and who the act leaves out
 * and why. Someone left out is not an error: "Sara Ahmed, auditor: no final
 * grade". A person is never matched by email or name to widen it.
 */
export interface BulkScope {
  selection: Selection;
  leftOut: { id: string; sentence: string }[];
}

/** The foot's line for a bulk scope: "3 selected, 1 not shown by this filter". */
export function scopeSentence(scope: BulkScope): string {
  const { keys, hidden } = scope.selection;
  return hidden ? `${keys.length} selected, ${hidden} not shown by this filter` : `${keys.length} selected`;
}

export interface Effect {
  id: string;
  /** "Publish the recap and next steps to 2 learners". */
  sentence: string;
  /** A named choice beside the line, never a silent default. */
  optional?: { checked: boolean; toggle: () => void };
  /** The part it comes from (a composite's step). */
  part?: PartId;
}

export interface Commit {
  /** "Close session", "Hand in attempt 2", "Bring in 9 learners". */
  verb: string;
  /** The caption above the effects: "When you close Session 9". */
  cap: string;
  /** Effects derive from the draft, so they claim only what it will do (O5). */
  effects: () => Effect[];
  /** The commit waits until empty; each links to its part. */
  blockers: () => Blocker[];
  /**
   * A revision: adds the typed unchanged blocker while `unchanged()` holds (O6).
   * `attempt` only for a hand-in, whose versions are attempts; a roll or a plan has none.
   */
  revises?: { attempt?: number; part: PartId; unchanged: () => boolean };
  /** A quiet line under the commit. */
  note?: string;
  /** Every commit sends a key; Check asks the server's receipt whether that key committed (W1). */
  run: (requestId: string) => Promise<Result>;
}

/**
 * Records a commit touched, and one sentence about them (W1, C3). A failure's
 * sentence is the server's, one a person can act on, and names the part that
 * fixes it when one does.
 */
export interface ResultLine {
  sentence: string;
  ids: string[];
  fix?: PartId;
}

/** A partial result's Retry or Check, run under the commit's own key. */
export interface PartialAct {
  verb: string;
  note: string;
  run: (requestId: string) => Promise<Result>;
}

/**
 * A commit's result, held in the box (W1, W2). Confirmed says what happened
 * and what is still open; partial names what failed and retries only that,
 * and checks what isn't known; refused keeps the draft and says why; unknown
 * offers Check, never a repeat, because the write may already have landed. A
 * confirmed or partial `cap` is optional and, when present, says what the
 * head doesn't (CASE-025).
 */
export type Result =
  | { state: "none" }
  /** `version` is the record's version this commit made: text kept in its tab draft past it begins there (CASE-038, R4). */
  | { state: "confirmed"; cap?: string; at: string; effects: string[]; remains: string[]; version?: string }
  | ({
      state: "partial";
      cap?: string;
      at: string;
      /** The ids that went through, and the ids that failed, each with a sentence. */
      went: ResultLine[];
      failed: ResultLine[];
      /** What is still open, as on a confirmed result. */
      remains?: string[];
      /** The commit's key. Retry resends only the failed ids, and Check reads the unsure ones, under this same key. */
      requestId: string;
    } & (
      | { unsure?: never; check?: never; retry: PartialAct }
      /**
       * Ids whose outcome isn't known (a send may have gone) are never resent:
       * Check reads them, and never sends. Retry stands beside it only when
       * some failed ids may be resent.
       */
      | { unsure: [ResultLine, ...ResultLine[]]; check: PartialAct; retry?: PartialAct }
    ))
  | { state: "refused"; cap: string; sentence: string; draft: "kept" }
  | {
      state: "unknown";
      cap: string;
      requestId: string;
      sentence: string;
      check: { verb: string; note: string; run: () => Promise<Result> };
    };

export type ResultState = Exclude<Result["state"], "none">;

/** Task: changes. Parts in time order, then one foot that states the effects and commits once (K2, O3). */
export interface TaskBox<T> extends BoxBase<T> {
  /** Dock this Task's one native foot in its active VisitFrame, never copy it. */
  footer?: "host";
  kind: "task";
  /** Called with the ready value, or a stale read's last value (drawn with its read time). */
  state(value: T): BoxState;
  /**
   * What the open head says, when a part then carries the resting word itself:
   * a resend rests on "Invitation expired", and opened its head says "Ready to
   * send" while the invitation part says it expired (CASE-030).
   */
  openState?(value: T): BoxState;
  /** In the order the work happens (O2). A composite has several, possibly from several owners. */
  parts: Part[];
  /** One commit. A composite is still one Task with one commit (K2). */
  commit: Commit;
  /** The latest result; the face states it once the box rests. */
  result: Result;
  /** Whether the box holds a draft, saved or not (F3, O7). */
  draft: boolean;
  /** A bulk Task: its scope is its List's selection, named in the foot (R6). */
  over?: BulkScope;
}

export type Box = FactBox<unknown> | ListBox<unknown, unknown> | TaskBox<unknown>;

/* ------------------------------------------------------------------ */
/* 3. The page                                                         */
/* ------------------------------------------------------------------ */

/**
 * A page lays its boxes in one column, in the job's time order (P1), and
 * grants at most one lead (P2). The box order is the array order and never
 * depends on the clock (P4). Intent is page state (P3).
 */
export interface BentoPage {
  /** The page's journey id, the scope for stable order across states (P4). */
  journey: string;
  boxes: Box[];
  /** The box the person opened or acted in; cleared when its act completes or the box is hidden. */
  intent: string | null;
}

/** A page the person may not see, or whose read can't resolve: one sentence and a way back (R1, CASE-018). */
export type PageRefusal = {
  /** Names nothing hidden: no name, count or role. */
  sentence: string;
} & (
  /** forbidden: the person may not see the page; unavailable: its own read failed before any box (CASE-022). */
  | { reason: "forbidden" | "unavailable"; back: { label: string; to: Route }; home?: never }
  /** A Home has no origin, so its refusal has no way back and the frame stays (CASE-032). Unavailable offers Try again, as a check. */
  | { reason: "unavailable"; home: true; retry: () => void; back?: never }
  /** A seat the person doesn't hold: the sentence names who fixes it, such as the registrar, and there is no retry. */
  | { reason: "forbidden"; home: true; back?: never; retry?: never }
);

/** The kit's own view of a box: whether it is open, and whether it holds a draft. */
export interface BoxView {
  open: boolean;
  draft: boolean;
}

/** The face state the kit draws for a read (F1, F2, R1 to R3), or null when the box's own state applies. */
export function readFaceState(read: Read<unknown>): BoxState | null {
  switch (read.status) {
    case "loading":
      return { class: "reading", word: "Reading" };
    case "failed":
      return { class: "failed", word: read.sentence };
    case "empty":
      return { class: "quiet", word: read.sentence };
    case "unread":
      return { class: "reading", word: read.sentence };
    default:
      return null;
  }
}

/** A Fact's answer in words: its figures, else its prose, else its detail. */
export function factAnswer<T>(box: FactBox<T>, value: T): string {
  const figures = box.figures?.(value);
  if (figures?.length) return figures.map((figure) => `${figure.value} ${figure.unit}`).join(", ");
  return box.prose?.(value) ?? box.detail(value);
}

/**
 * The box's one mark: the read's own state while it isn't ready, else the
 * box's state from its value. A ready or empty Fact has none; a stale Fact's
 * mark is its last answer, drawn as reading (CASE-023), and a Fact whose
 * answer is only its body or fields says it isn't refreshed, never nothing (F2).
 */
export function stateOf(box: Box): BoxState | null {
  const read = box.read;
  if (box.kind === "fact") {
    if (read.status === "stale") return { class: "reading", word: factAnswer(box, read.last) || "Not refreshed" };
    return read.status === "ready" || read.status === "empty" ? null : readFaceState(read);
  }
  if (read.status === "ready") return box.state(read.value);
  if (read.status === "stale") return box.state(read.last);
  return readFaceState(read);
}

/** The open head's state: a Task's own open word where it has one, else the box's state. */
export function openStateOf(box: Box): BoxState | null {
  if (box.kind === "task" && box.openState) {
    if (box.read.status === "ready") return box.openState(box.read.value);
    if (box.read.status === "stale") return box.openState(box.read.last);
  }
  return stateOf(box);
}

/**
 * Every blocker on a commit: the commit's own, the typed unchanged one, and
 * each part's conflict (O6, R4). A conflict's blocker names the choice in the
 * kit's words; the part's notice already says what changed (CASE-025).
 */
export function blockersOf(task: { commit: Commit; parts?: Part[] }): Blocker[] {
  const blockers = [...task.commit.blockers()];
  const revises = task.commit.revises;
  if (revises?.unchanged() && !blockers.some((b) => b.kind === "unchanged"))
    blockers.push({ kind: "unchanged", part: revises.part, attempt: revises.attempt });
  for (const part of task.parts ?? []) {
    if (part.conflict && !blockers.some((b) => b.kind === "conflict" && b.part === part.id))
      blockers.push({ kind: "conflict", part: part.id, sentence: `Choose which ${part.label.toLowerCase()} to keep.` });
  }
  return blockers;
}

/**
 * The page's one lead (P2). The person's choice owns it: if the box they
 * chose has an eligible candidate, that act leads, and if it is blocked,
 * nothing on the page is filled. Otherwise the eligible, unblocked candidate
 * with the lowest server rank in a box whose read is ready leads. An unranked
 * candidate leads only by the person's choice, never by rank (CASE-023). A
 * page may have no lead.
 */
export function leadOf(page: Pick<BentoPage, "boxes" | "intent">): string | null {
  const chosen = page.intent === null ? undefined : page.boxes.find((box) => box.id === page.intent);
  if (chosen?.candidate?.eligible) return chosen.candidate.blocked ? null : chosen.id;
  let lead: { id: string; rank: number } | null = null;
  for (const box of page.boxes) {
    const candidate = box.candidate;
    if (!candidate?.eligible || candidate.blocked || candidate.rank === null) continue;
    if (box.read.status !== "ready") continue;
    if (lead === null || candidate.rank < lead.rank) lead = { id: box.id, rank: candidate.rank };
  }
  return lead?.id ?? null;
}

/**
 * The kit decides when a List or Task rests as one line; a box can't claim it
 * (F3). It rests only when all six hold: it isn't open; it holds no draft; its
 * read is ready; its result is confirmed, or it has none; none of its parts is
 * in conflict; its state is done. A Fact never rests (CASE-023).
 */
export function restsSettled(box: Box, view: BoxView): boolean {
  if (box.kind === "fact") return false;
  if (view.open || box.fold === "never") return false;
  if (view.draft) return false;
  if (box.read.status !== "ready") return false;
  if (box.kind === "task") {
    if (box.result.state !== "none" && box.result.state !== "confirmed") return false;
    if (box.parts.some((part) => part.conflict)) return false;
  }
  return stateOf(box)?.class === "done";
}

/**
 * A confirmed result that folds the box is the settled face: it carries the
 * result and takes focus (W2).
 */
export function foldedResult(box: Box, view: BoxView): boolean {
  return box.kind === "task" && box.result.state === "confirmed" && restsSettled(box, view);
}

/**
 * Refuses a page the contract forbids: two boxes for one job, intent naming a
 * box that isn't there, or a box that says done over a doubtful result (W3).
 */
export function assertPage(page: BentoPage): void {
  const ids = page.boxes.map((box) => box.id);
  if (new Set(ids).size !== ids.length) throw new Error("One box per job");
  if (page.intent !== null && !ids.includes(page.intent)) throw new Error("Intent names a box that isn't on the page");
  for (const box of page.boxes) {
    if (box.kind !== "task" || stateOf(box)?.class !== "done") continue;
    if (box.result.state === "partial" || box.result.state === "refused" || box.result.state === "unknown")
      throw new Error(`${box.id}: a task can't say done while its last result is ${box.result.state}`);
  }
}

/** The frame's parts: the band, the person frame, the rail and the course tabs (CASE-006). */
export type FramePart = "band" | "person" | "rail" | "tabs";

/**
 * The hook a frame part carries. The frame isn't a box: it sits outside every
 * `data-box` and is placed by the page, never by a box.
 */
export function frameHook(part: FramePart): { "data-frame": FramePart } {
  return { "data-frame": part };
}
